import React, { useEffect, useMemo, useState } from "react";
import { formatDate, numberFormat } from "../../../util/araknerdlib";
import { useLedger } from "../../../DataStore/LedgerContext";
import { Skeleton, SkeletonCircle, SkeletonPill } from "../../../util/skeleton/Skeleton";
import { useCurrency } from "../../../util/SystemVariables";
import { Link } from "react-router-dom";

/* ------------ tiny helpers ------------ */
const totals = (lines) =>
  (lines || []).reduce(
    (t, l) => ({
      dr: t.dr + (Number(l.debit ?? l.dr) || 0),
      cr: t.cr + (Number(l.credit ?? l.cr) || 0),
    }),
    { dr: 0, cr: 0 }
  );

const entryAmount = (sum) => Math.max(Number(sum.dr) || 0, Number(sum.cr) || 0);

const groupByDate = (arr) =>
  (arr || []).reduce((acc, j) => {
    (acc[j.date] = acc[j.date] || []).push(j);
    return acc;
  }, {});

const look = (ref) => {
  const key = (ref || "").split("-")[0];
  switch (key) {
    case "INV":
      return { icon: "fa-file-invoice-dollar" };
    case "PAY":
      return { icon: "fa-money-bill-wave" };
    case "BILL":
      return { icon: "fa-file-invoice" };
    case "DEP":
      return { icon: "fa-university" };
    case "JRN":
      return { icon: "fa-book" };
    default:
      return { icon: "fa-receipt" };
  }
};


const pickLatestCreatedAt = (rows) => {
  // rows may contain created_at; pick the maximum
  let best = "";
  for (const r of rows || []) {
    const t = r?.created_at || "";
    if (!t) continue;
    // lexical compare works for "YYYY-MM-DD HH:mm:ss"
    if (!best || t > best) best = t;
  }
  return best;
};


const RecentLedgerCard = () => {
  const [expandedList, setExpandedList] = useState({});
  const [showAll, setShowAll] = useState(false);
  const currency = useCurrency();

  const { recentLedgers, loadingRecentLedgers } = useLedger();

  /* ---------------- New API shape -> component list ----------------
    recentLedgers = {
      groups: {
        "3": { meta: {...}, lines: [...], totals: {...} },
        "1": { meta: {...}, lines: [...], totals: {...} }
      },
      order: ["3","1"],
      count: 2
    }
  */
  const items = useMemo(() => {
    const groups = recentLedgers?.groups;
    const order = Array.isArray(recentLedgers?.order) ? recentLedgers.order : null;

    if (!groups || typeof groups !== "object") return [];

    const gids = order && order.length ? order : Object.keys(groups);

    const out = gids
      .map((gid) => {
        const g = groups[gid];
        if (!g) return null;

        const meta = g.meta || {};
        const linesRaw = Array.isArray(g.lines) ? g.lines : [];

        const createdAt =
          meta.created_at || pickLatestCreatedAt(linesRaw) || ""; // ✅ new

        const lines = linesRaw.map((r) => ({
          accountCode: r?.account?.account_number || "",
          accountName: r?.account?.account_name || "",
          dr: Number(r?.debit) || 0,
          cr: Number(r?.credit) || 0,
          memo: r?.description || "",
          createdAt: r?.created_at || "", // ✅ optional (if you ever want per-line timestamps)
        }));

        lines.sort((a, b) => {
          const ad = a.dr > 0 ? 0 : 1;
          const bd = b.dr > 0 ? 0 : 1;
          return ad - bd;
        });

        const memo = meta.description || linesRaw?.[0]?.description || "";

        return {
          id: String(meta.ledger_grouping || gid),
          date: meta.transaction_date || linesRaw?.[0]?.transaction_date || "",
          ref: meta.transaction_ref || "",
          memo,
          createdAt, // ✅ new
          lines,
          totals: g.totals || null,
        };
      })
      .filter(Boolean);

    if (!order) {
      out.sort(
        (a, b) =>
          (b.date || "").localeCompare(a.date || "") ||
          String(b.id).localeCompare(String(a.id))
      );
    }

    return out;
  }, [recentLedgers]);


  const max = 10;
  const visible = showAll ? items : items.slice(0, max);
  const extraCount = Math.max(0, items.length - max);

  const groups = useMemo(() => groupByDate(visible), [visible]);
  const dates = useMemo(() => Object.keys(groups).sort((a, b) => b.localeCompare(a)), [groups]);

  /* ---------------- Skeleton ---------------- */
  if (loadingRecentLedgers || !recentLedgers) {
    return (
      <div className="card rac-card mb-3">
        <div className="rac-head d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <div className="rac-headIcon">
              <i className="fa fa-book-open" />
            </div>
            <div>
              <h6 className="rac-title">Recent Ledger Activity</h6>
              <div className="rac-sub">Latest journal entries posted</div>
            </div>
          </div>

          <span className="rac-action" style={{ opacity: 0.6 }}>
            View all <i className="fa fa-arrow-right" />
          </span>
        </div>

        <div className="p-4">
          {[...Array(3)].map((_, gi) => (
            <div key={gi} className="mb-3">
              <Skeleton w="120px" h={12} className="mb-2" />
              {[...Array(2)].map((__, ri) => (
                <div key={ri} className="py-2">
                  <div className="d-flex align-items-center">
                    <SkeletonCircle size={36} />
                    <div className="ml-3 flex-grow-1">
                      <Skeleton w="260px" h={16} />
                      <Skeleton w="220px" h={12} style={{ marginTop: 6 }} />
                      <div className="d-flex mt-2">
                        <SkeletonPill w={90} h={24} className="mr-2" />
                        <SkeletonPill w={100} h={24} />
                      </div>
                    </div>
                  </div>
                  <hr className="my-2" />
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="rac-foot">
          <div className="text-muted" style={{ fontSize: 12 }}>
            Showing <strong>0</strong> of <strong>0</strong>
          </div>
          <button className="rac-btn" type="button" disabled>
            Show more
          </button>
        </div>
      </div>
    );
  }

  /* ---------------- Render ---------------- */
  return (
    <div className="card rac-card mt-4">
      {/* Header */}
      <div className="rac-head d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <div className="rac-headIcon">
            <i className="fa fa-book-open" />
          </div>
          <div>
            <h6 className="rac-title">Recent Ledger Activity</h6>
            <div className="rac-sub">Latest journal entries posted</div>
          </div>
        </div>

        <Link to="/transaction-ledger" className="rac-action">
          View all <i className="fa fa-arrow-right" />
        </Link>
      </div>

      {/* Body */}
      <div className="card-body p-4">
        <div className="overflow-auto" style={{ maxHeight: 600, minHeight: 600 }}>
          {dates.map((date) => (
            <div key={date} className="mb-3">
              <div className="rac-date">{formatDate(date)}</div>

              <ul className="list-unstyled mb-0">
                {groups[date].map((entry) => {
                  // if ref isn't sent yet, icon defaults nicely
                  const meta = look(entry.ref);

                  // prefer API totals (already strings) but compute to be safe
                  const sum = entry.totals
                    ? { dr: Number(entry.totals.dr) || 0, cr: Number(entry.totals.cr) || 0 }
                    : totals(entry.lines);

                  const isBalanced =
                    entry.totals && typeof entry.totals.balanced === "boolean"
                      ? entry.totals.balanced
                      : Math.abs(sum.dr - sum.cr) < 0.0001;

                  const twoLine = entry.lines.length === 2;

                  const debit = twoLine ? entry.lines.find((l) => l.dr > 0) : null;
                  const credit = twoLine ? entry.lines.find((l) => l.cr > 0) : null;

                  const amount = twoLine
                    ? Number(debit?.dr) || Number(credit?.cr) || 0
                    : entryAmount(sum);

                  const open = !!expandedList[entry.id];

                  return (
                    <li key={entry.id} className="py-2">
                      <div className="rac-row">
                        <div className="rac-doc">
                          <i className={`fa ${meta.icon}`} />
                        </div>

                        <div className="ml-3 flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="rac-titleline">
                            {twoLine ? (
                              <>
                                <span className="rac-code">{debit?.accountCode || "—"}</span>
                                <span className="rac-acc">{debit?.accountName || "Unknown"}</span>

                                <i className="fa fa-arrow-right mx-2 text-muted" style={{ opacity: 0.45 }} />

                                <span className="rac-code">{credit?.accountCode || "—"}</span>
                                <span className="rac-acc">{credit?.accountName || "Unknown"}</span>
                              </>
                            ) : (
                              <>Compound entry · {entry.lines.length} lines</>
                            )}
                          </div>

                          <div className="rac-memo">{entry.memo}</div>

                          <div className="mt-2 d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                          {!!entry.ref && <span className="rac-ref text-monospace">{entry.ref}</span>}

                          {/* <span className={`rac-pill ${isBalanced ? "is-ok" : "is-bad"}`}>
                            <i className={`fa ${isBalanced ? "fa-check-circle" : "fa-triangle-exclamation"}`} />
                            {isBalanced ? "Balanced" : "Out of balance"}
                          </span> */}

                          {/* ✅ Timestamp chip */}
                          {!!entry.createdAt && (
                            <span className="rac-timeChip" title={entry.createdAt}>
                              <i className="fa fa-clock mr-1" style={{ opacity: 0.7 }} />
                              {formatDate(entry.createdAt)}
                            </span>
                          )}

                          <span className="rac-pill is-ok">
                            {currency} {numberFormat(amount)}
                          </span>

                          <Link
                            to={`/transaction-ledger/${entry.id}`}
                            className="rac-detail-link"
                            title="View transaction details"
                          >
                            Details <i className="fa fa-chevron-right" />
                          </Link>
                        </div>


                          {!twoLine && (
                            <button
                              className="btn btn-link btn-sm p-0 mt-2"
                              onClick={() => setExpandedList((s) => ({ ...s, [entry.id]: !open }))}
                            >
                              {open ? "Hide lines" : "Show lines"}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* detail lines for compound entries */}
                      {!twoLine && open && (
                        <div className="mt-3 ml-5">
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead className="thead-light">
                                <tr>
                                  <th style={{ width: 90 }}>A/C</th>
                                  {/* <th>Account</th> */}
                                  <th className="text-right">Debit</th>
                                  <th className="text-right">Credit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {entry.lines.map((l, idx) => (
                                  <tr key={idx}>
                                    <td className="text-monospace">{l.accountCode}</td>
                                    {/* <td>{l.accountName}</td> */}
                                    <td className="text-right">{l.dr ? numberFormat(l.dr) : ""}</td>
                                    <td className="text-right text-danger">{l.cr ? numberFormat(l.cr) : ""}</td>
                                  </tr>
                                ))}
                                <tr className="font-weight-bold">
                                  <td>Total</td>
                                  <td className="text-right">{numberFormat(sum.dr)}</td>
                                  <td className="text-right">{numberFormat(sum.cr)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      <hr className="my-3" />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      {items.length > 0 && (
        <div className="rac-foot">
          <div className="text-muted" style={{ fontSize: 12 }}>
            Showing <strong>{visible.length}</strong> of <strong>{items.length}</strong>
          </div>

          {items.length > max && (
            <button type="button" className="rac-btn" onClick={() => setShowAll((s) => !s)}>
              {showAll ? "Show less" : `Show ${extraCount} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RecentLedgerCard;
